import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  Eye,
  EyeOff,
  Info,
  Loader2,
  Lock,
  User,
} from 'lucide-react';
import type { AuthMode } from './authMode';
import './loginPage.css';

const MARCH_HEALTH_LOGO_SRC = '/icons/march_icon.png';

type LoginPageProps = {
  mode: AuthMode;
  onRedirectLogin: (options: { staySignedIn: boolean }) => Promise<void>;
  onPasswordLogin: (
    username: string,
    password: string,
    options: { rememberUsername: boolean; staySignedIn: boolean },
  ) => Promise<void>;
  error?: string | null;
  onClearError?: () => void;
  configReady: boolean;
  smartLaunchPending?: boolean;
  initialUsername?: string;
  initialStaySignedIn?: boolean;
};

/** Map technical failures to user-safe messages without changing auth flow. */
function readableAuthError(message: string): string {
  const trimmed = message.trim();
  // An empty message means we know nothing -- not that the password was wrong.
  if (!trimmed) return 'Sign-in failed. Please try again, or contact your March Health administrator if it continues.';

  const lower = trimmed.toLowerCase();

  if (
    lower.includes('password sign-in is not enabled') ||
    lower.includes('sign-in client is misconfigured') ||
    lower.includes('sign-in request is misconfigured') ||
    lower.includes('sign-in service is temporarily unavailable') ||
    lower.includes('sign-in is temporarily unavailable') ||
    lower.includes('sign-in service rejected') ||
    lower.includes('account is disabled') ||
    lower.includes('email is not verified') ||
    lower.includes('do not have permission')
  ) {
    return trimmed;
  }

  if (
    lower.includes('smart') ||
    lower.includes('session_id') ||
    lower.includes('launch context') ||
    lower.includes('smart panel')
  ) {
    return 'Unable to open your workspace from this link. Please return to the clinical panel and launch again.';
  }

  if (
    trimmed.startsWith('{') ||
    trimmed.includes('access_token') ||
    trimmed.includes('refresh_token') ||
    lower.includes('unexpected token') ||
    lower.includes('keycloak') ||
    lower.includes('invalid_grant') ||
    lower.includes('invalid user') ||
    lower.includes('unauthorized') ||
    lower.includes('401') ||
    lower.includes('403') ||
    lower.includes('login failed')
  ) {
    return 'Invalid username or password. Please try again.';
  }

  // Transport failures. `Unable to reach the sign-in service` is what
  // keycloakPasswordClient returns from its own catch, and it used to fall
  // through to the credentials default below -- so a CORS failure was reported
  // as a wrong password while the server was returning 200. Hours were spent on
  // that, by two people, before a Network tab settled it.
  if (
    lower.includes('failed to fetch') ||
    lower.includes('networkerror') ||
    lower.includes('unable to reach') ||
    lower.includes('load failed') ||
    lower.includes('cors')
  ) {
    return 'Unable to reach the sign-in service. Please check your connection, or contact your March Health administrator if it continues.';
  }

  if (
    lower.includes('vite_') ||
    lower.includes('development iam') ||
    lower.includes('configure') ||
    lower.includes('token response')
  ) {
    return 'Sign-in is temporarily unavailable. Please contact your March Health administrator.';
  }

  if (
    lower.includes('username and password are required') ||
    lower.includes('please enter your username and password')
  ) {
    return 'Please enter your username and password.';
  }

  // DEFAULT: say that sign-in failed, NOT why.
  //
  // This used to return "Invalid username or password", which asserts a
  // specific cause for any failure the branches above did not recognise --
  // including transport errors, misconfiguration and server faults. A wrong
  // password still gets the precise message from the `invalid_grant` branch
  // above; everything else now gets an honest one. Guessing the cause sends
  // people to re-type a password that was never wrong.
  return 'Sign-in failed. Please try again, or contact your March Health administrator if it continues.';
}

function isCredentialFailure(message: string | null | undefined): boolean {
  const lower = String(message ?? '').trim().toLowerCase();
  if (!lower) return false;

  return (
    lower.includes('invalid username') ||
    lower.includes('invalid password') ||
    lower.includes('invalid user credentials') ||
    lower.includes('invalid username or password') ||
    lower.includes('invalid_grant') ||
    lower.includes('unauthorized') ||
    lower.includes('401') ||
    lower.includes('login failed')
  );
}

export function LoginPage({
  mode,
  onRedirectLogin,
  onPasswordLogin,
  error,
  onClearError,
  configReady,
  smartLaunchPending = false,
  initialUsername = '',
  initialStaySignedIn = false,
}: LoginPageProps) {
  const [username, setUsername] = useState(initialUsername);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberUsername, setRememberUsername] = useState(Boolean(initialUsername));
  const [staySignedIn, setStaySignedInState] = useState(initialStaySignedIn);
  const [pending, setPending] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [authAttempted, setAuthAttempted] = useState(false);

  const passwordMode = mode === 'dev_keycloak_password';
  const externalError = authAttempted || !isCredentialFailure(error) ? error : null;
  const hasError = Boolean(formError || externalError);
  const displayError = hasError ? readableAuthError(formError || externalError || '') : '';

  useEffect(() => {
    onClearError?.();
  }, [onClearError]);

  const handlePasswordSubmit = useCallback(async () => {
    if (!configReady) return;
    const user = username.trim();
    const pass = password;
    if (!user || !pass) {
      setAuthAttempted(false);
      setFormError('Please enter your username and password.');
      return;
    }

    setAuthAttempted(true);
    setPending(true);
    setFormError(null);
    setPassword('');
    try {
      await onPasswordLogin(user, pass, {
        rememberUsername,
        staySignedIn,
      });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Sign in failed.');
    } finally {
      setPending(false);
    }
  }, [configReady, username, password, rememberUsername, staySignedIn, onPasswordLogin]);

  const handleRedirect = async () => {
    setAuthAttempted(true);
    setPending(true);
    setFormError(null);
    try {
      await onRedirectLogin({ staySignedIn });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Sign in failed.');
      setPending(false);
    }
  };

  const onFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordMode) void handlePasswordSubmit();
  };

  const submitDisabled =
    !configReady ||
    pending ||
    (passwordMode ? !username.trim() || !password : false);

  return (
    <div className="march-login-root">
      <div className="march-login-root__bg-gradient" aria-hidden />
      <div className="march-login-root__bg-glow" aria-hidden />
      <div className="march-login-root__bg-grid" aria-hidden />

      <div className="march-login-root__center">
        <div className="march-login-card" role="main" aria-labelledby="march-login-title">
          <div className="march-login-card__accent" aria-hidden />
          <div className="march-login-card__body">
            <div className="march-login-card__header">
              <span className="march-login-badge">Secure Access</span>

              <div className="march-login-brand">
                <div className="march-login-brand__logo-wrap">
                  <div className="march-login-brand__logo-glow" aria-hidden />
                  <img
                    src={MARCH_HEALTH_LOGO_SRC}
                    alt="March Health"
                    className="march-login-brand__logo"
                    width={56}
                    height={56}
                  />
                </div>
                <div className="march-login-brand__org">MARCH HEALTH</div>
                <div className="march-login-brand__product">MarchAgent</div>
              </div>

              <h1 id="march-login-title" className="march-login-title">
                Sign in to MarchAgent
              </h1>
              <p className="march-login-subtitle">Continue to your MarchAgent workspace.</p>

              {smartLaunchPending ? (
                <div className="march-login-callout" role="status">
                  <Info className="march-login-callout__icon" size={16} aria-hidden />
                  <p className="march-login-callout__text">
                    Your clinical workspace is ready. Sign in to continue securely.
                  </p>
                </div>
              ) : null}
            </div>

            {hasError && displayError ? (
              <div
                className="march-login-alert march-login-alert--error"
                role="alert"
                aria-live="polite"
                id="march-login-error"
                style={{ marginTop: smartLaunchPending ? 20 : 28 }}
              >
                <AlertCircle size={16} aria-hidden style={{ flexShrink: 0, marginTop: 2 }} />
                <span>{displayError}</span>
              </div>
            ) : null}

            {!configReady ? (
              <div
                className="march-login-alert march-login-alert--warn"
                role="status"
                style={{ marginTop: 28 }}
              >
                Sign-in is temporarily unavailable. Please contact your March Health administrator.
              </div>
            ) : null}

            {passwordMode ? (
              <form onSubmit={onFormSubmit} className="march-login-form" noValidate>
                <div className="march-login-field">
                  <label htmlFor="march-login-username" className="march-login-label">
                    Username
                  </label>
                  <div className="march-login-input-wrap">
                    <User className="march-login-input-icon" aria-hidden />
                    <input
                      id="march-login-username"
                      name="username"
                      type="text"
                      autoComplete="username"
                      autoFocus={configReady}
                      disabled={pending || !configReady}
                      value={username}
                      onChange={(e) => {
                        setUsername(e.target.value);
                        setFormError(null);
                        setAuthAttempted(false);
                        onClearError?.();
                      }}
                      className="march-login-input march-login-input--with-icon"
                      placeholder="Enter your username"
                      aria-invalid={hasError}
                      aria-describedby={hasError ? 'march-login-error' : undefined}
                    />
                  </div>
                </div>

                <div className="march-login-field">
                  <label htmlFor="march-login-password" className="march-login-label">
                    Password
                  </label>
                  <div className="march-login-input-wrap">
                    <Lock className="march-login-input-icon" aria-hidden />
                    <input
                      id="march-login-password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      disabled={pending || !configReady}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setFormError(null);
                        setAuthAttempted(false);
                        onClearError?.();
                      }}
                      className="march-login-input march-login-input--with-icon march-login-input--password"
                      placeholder="Enter your password"
                      aria-invalid={hasError}
                      aria-describedby={hasError ? 'march-login-error' : undefined}
                    />
                    <button
                      type="button"
                      className="march-login-toggle"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      disabled={pending || !configReady}
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword ? (
                        <EyeOff size={16} aria-hidden />
                      ) : (
                        <Eye size={16} aria-hidden />
                      )}
                    </button>
                  </div>
                </div>

                <div className="march-login-options">
                  <label className="march-login-checkbox">
                    <input
                      type="checkbox"
                      checked={rememberUsername}
                      disabled={pending || !configReady}
                      onChange={(e) => setRememberUsername(e.target.checked)}
                    />
                    <span>Remember my username</span>
                  </label>
                  <label className="march-login-checkbox">
                    <input
                      type="checkbox"
                      checked={staySignedIn}
                      disabled={pending || !configReady}
                      onChange={(e) => setStaySignedInState(e.target.checked)}
                    />
                    <span>Stay signed in on this device</span>
                  </label>
                </div>

                <button
                  type="submit"
                  className="march-login-submit"
                  disabled={submitDisabled}
                  aria-busy={pending}
                >
                  {pending ? (
                    <>
                      <Loader2 size={16} className="march-login-spin" aria-hidden />
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </button>
              </form>
            ) : (
              <div className="march-login-form">
                <div className="march-login-options">
                  <label className="march-login-checkbox">
                    <input
                      type="checkbox"
                      checked={staySignedIn}
                      disabled={pending || !configReady}
                      onChange={(e) => setStaySignedInState(e.target.checked)}
                    />
                    <span>Stay signed in on this device</span>
                  </label>
                </div>
                <button
                  type="button"
                  className="march-login-submit"
                  disabled={submitDisabled}
                  aria-busy={pending}
                  onClick={() => void handleRedirect()}
                >
                  {pending ? (
                    <>
                      <Loader2 size={16} className="march-login-spin" aria-hidden />
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </button>
              </div>
            )}
          </div>

          <footer className="march-login-footer">
            <div className="march-login-footer__inner">
              <Lock className="march-login-footer__icon" aria-hidden />
              <span>Access is restricted to authorized March Health users.</span>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}
